import { useCallback, useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// useUserBlocks — manages the current user's block list. Blocked users'
// content (e.g. comments) is filtered out of the UI client-side; RLS still
// permits the rows to be fetched, so the filtering is purely cosmetic.

export function useUserBlocks() {
  const { user } = useAuth();
  const [blocked, setBlocked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setBlocked(new Set()); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from("user_blocks")
      .select("blocked_id")
      .eq("blocker_id", user.id);
    setLoading(false);
    if (error) return;
    setBlocked(new Set((data ?? []).map((r) => r.blocked_id)));
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  const block = useCallback(async (targetId: string, displayName?: string) => {
    if (!user) { toast.error("Sign in to block users"); return false; }
    if (targetId === user.id) { toast.error("You can't block yourself"); return false; }
    setBlocked((cur) => new Set(cur).add(targetId));
    const { error } = await supabase
      .from("user_blocks")
      .insert({ blocker_id: user.id, blocked_id: targetId });
    if (error && error.code !== "23505") {
      setBlocked((cur) => { const n = new Set(cur); n.delete(targetId); return n; });
      toast.error("Couldn't block that user");
      return false;
    }
    toast.success(displayName ? `Blocked ${displayName}` : "User blocked");
    return true;
  }, [user]);

  const unblock = useCallback(async (targetId: string) => {
    if (!user) return false;
    const prev = new Set(blocked);
    setBlocked((cur) => { const n = new Set(cur); n.delete(targetId); return n; });
    const { error } = await supabase
      .from("user_blocks")
      .delete()
      .eq("blocker_id", user.id)
      .eq("blocked_id", targetId);
    if (error) {
      setBlocked(prev);
      toast.error("Couldn't unblock that user");
      return false;
    }
    toast.success("User unblocked");
    return true;
  }, [user, blocked]);

  return {
    blockedIds: blocked,
    isBlocked: (id: string) => blocked.has(id),
    block,
    unblock,
    loading,
    refresh: load,
  };
}